import { datadogRum } from '@datadog/browser-rum';
import { ApiBookingT } from 'types/booking-type';
import { numberOfDaysBetweenTwoDate } from 'utils/date-helper';
import { setItem } from 'utils/local-storage-helpers';
import { Steps } from '../container/PricingJockeySection/Default/pricing-jockey-section-types';

// ── Event name registry ──────────────────────────────────────────────────────
// Single source of truth for all event names pushed to the dataLayer.
// Update GTM triggers to match these names when deploying.

export const EVENT = {
  CHECKOUT: 'ector_checkout',
  CHECKOUT_OPTION: 'ector_checkout_option',
  ADD_PRODUCT: 'ector_add_product',
  IMPRESSION: 'ector_impression',
  LOGIN: 'ector_login',
  LOGOUT: 'ector_logout',
  FLEET_STEP: 'ector_fleet_step',
  EVENT_MODAL_DISPLAY: 'ector_event_modal_display',
} as const;

// Legacy event names kept for backward compatibility with existing GTM triggers.
// Remove once all triggers are migrated to the EVENT names above.
const LEGACY_EVENT = {
  CHECKOUT: 'checkout',
  CHECKOUT_OPTION: 'checkoutOption',
  ADD_PRODUCT: 'addProduct',
  IMPRESSION: 'addImpression',
  LOGIN: 'login',
  LOGOUT: 'logout',
  FLEET_STEP: 'fleetStep',
} as const;

// ── Public product / impression types ────────────────────────────────────────

export interface GTMImpressionType {
  name: string;
  price: string | number;
  list: string;
  id?: string;
  category?: string;
  variant?: string;
}

export interface GTMProductType {
  name: string;
  id?: string;
  price: string | number;
  category?: string;
  variant?: string;
  list?: string;
}

// ── Per-event payload types (discriminated union) ────────────────────────────

type BookingAmplitude = {
  startAt?: string | null;
  endAt?: string | null;
  fromZoneCode?: string | null;
  toZoneCode?: string | null;
  reason?: string | null;
  duration?: number;
  summaryPrices?: { totalPrice: string | number } | null;
};

type CheckoutEvent = {
  event: typeof EVENT.CHECKOUT;
  step: 1 | 2 | 3 | 4;
  stepName: 'Start Pricing Form' | 'Price Display' | 'Popup Display' | 'Options Display';
  option: string | boolean | null;
  products: GTMProductType[];
  amplitude: BookingAmplitude;
};

type CheckoutOptionEvent = {
  event: typeof EVENT.CHECKOUT_OPTION;
  step: number;
  option: string;
};

type AddProductEvent = {
  event: typeof EVENT.ADD_PRODUCT;
  action: string;
  label: string;
  value: string | number;
  product: GTMProductType;
};

type ImpressionEvent = {
  event: typeof EVENT.IMPRESSION;
  action: string;
  impressions: GTMImpressionType[];
};

type LoginEvent = {
  event: typeof EVENT.LOGIN;
  user: { email: string };
};

type LogoutEvent = {
  event: typeof EVENT.LOGOUT;
  askedFor: boolean;
};

type EventModalDisplayEvent = {
  event: typeof EVENT.EVENT_MODAL_DISPLAY;
  isLoggedIn: boolean;
};

type FleetStepEvent = {
  event: typeof EVENT.FLEET_STEP;
  step: Steps | 'FleetPayment' | 'FleetPaymentError' | 'FleetPaymentSuccess';
  amplitude: Record<string, unknown>;
};

type TrackingEvent =
  | CheckoutEvent
  | CheckoutOptionEvent
  | AddProductEvent
  | ImpressionEvent
  | LoginEvent
  | LogoutEvent
  | FleetStepEvent
  | EventModalDisplayEvent;

// ── Internal helpers ─────────────────────────────────────────────────────────

const getBookingProducts = (booking: ApiBookingT): GTMProductType[] => {
  const products: GTMProductType[] = [
    {
      id: booking.id || '',
      price: booking.summaryPrices ? booking.summaryPrices.totalPrice : '',
      name: booking.fromSpot ? booking.fromSpot.zone.code.toUpperCase() : '',
      category: numberOfDaysBetweenTwoDate(booking.startAt || '', booking.endAt || '').toString(),
      list: booking.fromSpot ? booking.fromSpot.type : '',
      variant: booking.disallowedReason || 'allowed',
    },
  ];

  if (booking.subscribedServices) {
    booking.subscribedServices.forEach(serviceCode => {
      const foundService = booking.availableServices.find(s => s.code === serviceCode);
      if (foundService) {
        products.push({
          id: foundService.code,
          name: foundService.name,
          price: foundService.price,
          category: foundService.category,
          list: 'Service',
        });
      }
    });
  }

  return products;
};

const getAmplitudeData = (booking: ApiBookingT): BookingAmplitude => {
  const { summaryPrices, isBusiness, startAt, endAt, fromZone, toZone } = booking;

  return {
    summaryPrices,
    duration: numberOfDaysBetweenTwoDate(startAt || '', endAt || ''),
    reason: isBusiness ? 'work' : 'leisure',
    fromZoneCode: fromZone ? fromZone.code : null,
    toZoneCode: toZone ? toZone.code : null,
    startAt,
    endAt,
  };
};

// ── Dispatcher ───────────────────────────────────────────────────────────────

const track = (e: TrackingEvent): void => {
  switch (e.event) {
    case EVENT.CHECKOUT: {
      const checkoutEvent = `${EVENT.CHECKOUT}_${e.stepName.toLowerCase().replace(/ /g, '_')}`;
      const payload = {
        eventCategory: 'Ecommerce',
        eventAction: 'Funnel Step',
        eventLabel: e.stepName,
        amplitude: e.amplitude,
        ecommerce: {
          checkout: {
            actionField: { step: e.step, name: e.stepName, option: e.option },
            products: e.products,
          },
        },
      };
      window?.dataLayer?.push({ event: checkoutEvent, ...payload });
      window?.dataLayer?.push({ event: LEGACY_EVENT.CHECKOUT, ...payload });
      break;
    }

    case EVENT.CHECKOUT_OPTION: {
      const payload = {
        ecommerce: {
          checkout_option: {
            actionField: { step: e.step, option: e.option },
          },
        },
      };
      window?.dataLayer?.push({ event: e.event, ...payload });
      window?.dataLayer?.push({ event: LEGACY_EVENT.CHECKOUT_OPTION, ...payload });
      break;
    }

    case EVENT.ADD_PRODUCT: {
      const payload = {
        eventCategory: 'Ecommerce',
        eventAction: e.action,
        eventLabel: e.label,
        eventValue: e.value,
        ecommerce: {
          currencyCode: 'EUR',
          add: { products: [e.product] },
        },
      };
      window?.dataLayer?.push({ event: e.event, ...payload });
      window?.dataLayer?.push({ event: LEGACY_EVENT.ADD_PRODUCT, ...payload });
      break;
    }

    case EVENT.IMPRESSION: {
      const payload = {
        eventCategory: 'Ecommerce',
        eventAction: e.action,
        ecommerce: {
          currencyCode: 'EUR',
          impressions: e.impressions,
        },
      };
      window?.dataLayer?.push({ event: e.event, ...payload });
      window?.dataLayer?.push({ event: LEGACY_EVENT.IMPRESSION, ...payload });
      break;
    }

    case EVENT.LOGIN: {
      if (!window?.dataLayer) break;
      const lastEvent = window.dataLayer[window.dataLayer.length - 1];
      const isRecentLogin =
        lastEvent?.event === EVENT.LOGIN || lastEvent?.event === LEGACY_EVENT.LOGIN;
      if (!isRecentLogin) {
        window.dataLayer.push({ event: e.event, user: e.user });
        window.dataLayer.push({ event: LEGACY_EVENT.LOGIN, user: e.user });
      }
      break;
    }

    case EVENT.LOGOUT: {
      const payload = { amplitude: { askedFor: e.askedFor } };
      window?.dataLayer?.push({ event: e.event, ...payload });
      window?.dataLayer?.push({ event: LEGACY_EVENT.LOGOUT, ...payload });
      break;
    }

    case EVENT.EVENT_MODAL_DISPLAY:
      window?.dataLayer?.push({ event: e.event, isLoggedIn: e.isLoggedIn });
      break;

    case EVENT.FLEET_STEP: {
      const fleetStepEvent = `${EVENT.FLEET_STEP}_${e.step}`;
      const legacyFleetStepEvent = `${LEGACY_EVENT.FLEET_STEP}-${e.step}`;
      const payload = {
        eventCategory: 'Fleet',
        eventAction: 'Fleet Step',
        eventLabel: `Step ${e.step}`,
        amplitude: e.amplitude,
      };
      datadogRum.addAction(fleetStepEvent, payload);
      window?.dataLayer?.push({ event: fleetStepEvent, ...payload });
      window?.dataLayer?.push({ event: legacyFleetStepEvent, ...payload });
      break;
    }
  }
};

// ── Public API (call-site signatures unchanged) ──────────────────────────────

export const onCheckoutStartPricingForm = () =>
  track({
    event: EVENT.CHECKOUT,
    step: 1,
    stepName: 'Start Pricing Form',
    option: null,
    products: [],
    amplitude: {},
  });

export const onCheckoutPriceDisplay = (booking: ApiBookingT) =>
  track({
    event: EVENT.CHECKOUT,
    step: 2,
    stepName: 'Price Display',
    option: !booking.allowed ? (booking.disallowedReason ?? null) : 'allowed',
    products: getBookingProducts(booking),
    amplitude: getAmplitudeData(booking),
  });

export const onCheckoutPopup = (booking: ApiBookingT) =>
  track({
    event: EVENT.CHECKOUT,
    step: 3,
    stepName: 'Popup Display',
    option: 'not-displayed',
    products: getBookingProducts(booking),
    amplitude: getAmplitudeData(booking),
  });

export const onUpdateCheckoutPopup = (booking: ApiBookingT) => {
  const option = booking.allowed
    ? 'not-displayed'
    : booking.alternatesStartDate.length === 2
      ? 'multiple'
      : 'single';

  track({ event: EVENT.CHECKOUT_OPTION, step: 3, option });
};

export const onCheckoutOptionsDisplay = (booking: ApiBookingT) =>
  track({
    event: EVENT.CHECKOUT,
    step: 4,
    stepName: 'Options Display',
    option: null,
    products: getBookingProducts(booking),
    amplitude: {},
  });

export const addProduct = (
  action: string,
  label: string,
  value: string | number,
  product: GTMProductType,
) => track({ event: EVENT.ADD_PRODUCT, action, label, value, product });

export const addBookingToCart = (booking: ApiBookingT) =>
  addProduct(
    'Add Booking',
    booking.fromSpot ? booking.fromSpot.zone.code.toUpperCase() : '',
    booking.summaryPrices.totalPrice,
    {
      id: booking.id,
      name: booking.fromSpot ? booking.fromSpot.zone.code.toUpperCase() : '',
      price: booking.summaryPrices.totalPrice,
      variant: booking.disallowedReason || 'allowed',
      category: numberOfDaysBetweenTwoDate(booking.startAt, booking.endAt).toString(),
      list: booking.fromSpot ? booking.fromSpot.type : '',
    },
  );

export const onDisplayBooking = (booking: ApiBookingT) =>
  track({
    event: EVENT.IMPRESSION,
    action: 'Display Booking',
    impressions: [
      {
        id: booking.id,
        price: booking.summaryPrices ? booking.summaryPrices.totalPrice : '',
        name: booking.fromSpot ? booking.fromSpot.zone.code.toUpperCase() : '',
        category: numberOfDaysBetweenTwoDate(booking.startAt, booking.endAt).toString(),
        list: booking.fromSpot ? booking.fromSpot.type : '',
        variant: booking.disallowedReason || 'allowed',
      },
    ],
  });

export const onLogin = (user: { email: string }) => {
  setItem('email', user.email);
  track({ event: EVENT.LOGIN, user });
};

export const onValidateFleetStep = (
  step: Steps | 'FleetPayment' | 'FleetPaymentError' | 'FleetPaymentSuccess',
  amplitude: Record<string, unknown> = {},
) => track({ event: EVENT.FLEET_STEP, step, amplitude });

export const onEventModalDisplay = (isLoggedIn: boolean) =>
  track({ event: EVENT.EVENT_MODAL_DISPLAY, isLoggedIn });

export const onLogout = (askedFor = false) => track({ event: EVENT.LOGOUT, askedFor });
